import { getApiUrl } from "@dashboard/config";
import useNotifier from "@dashboard/hooks/useNotifier";
import { createFetch } from "@saleor/sdk";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";

export type OrderExportFileType = "csv" | "xlsx";

export interface OrderExportOptions {
  fileType?: OrderExportFileType;
  ids?: string[];
  filter?: Record<string, unknown>;
  fields?: string[];
  drafts?: boolean;
}

const COLUMN_TO_EXPORT_FIELDS: Record<string, string[]> = {
  number: ["number"],
  date: ["created"],
  customer: ["customer"],
  payment: ["payment status"],
  status: ["status"],
  total: ["total gross", "currency"],
};

export const getExportFields = (columns: string[]): string[] => [
  ...new Set(columns.flatMap(column => COLUMN_TO_EXPORT_FIELDS[column] ?? [])),
];

const getExportUrl = () => new URL("/orders/export/", getApiUrl()).toString();

const getFileNameFromResponse = (response: Response, fallback: string) => {
  const disposition = response.headers.get("content-disposition");
  const match = disposition?.match(/filename="?([^";]+)"?/);

  return match?.[1] ?? fallback;
};

const saveFile = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const useOrderExport = () => {
  const notify = useNotifier();
  const intl = useIntl();
  const [loading, setLoading] = useState(false);

  const exportOrders = useCallback(
    async ({ fileType = "csv", ...options }: OrderExportOptions = {}) => {
      setLoading(true);

      try {
        const authFetch = createFetch({
          refreshOnUnauthorized: false,
        }) as typeof fetch;
        const response = await authFetch(getExportUrl(), {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ fileType, ...options }),
        });

        if (!response.ok) {
          const details = (await response.json().catch(() => null)) as {
            error?: string;
          } | null;

          throw new Error(
            details?.error ?? `Export failed (${response.status})`,
          );
        }

        saveFile(
          await response.blob(),
          getFileNameFromResponse(response, `orders.${fileType}`),
        );

        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Orders exported",
            id: "KTxmIC",
            description: "notification",
          }),
        });
      } catch (error) {
        notify({
          status: "error",
          text:
            error instanceof Error
              ? error.message
              : intl.formatMessage({
                  defaultMessage: "Could not export orders",
                  id: "r/ZTLG",
                  description: "notification",
                }),
        });
      } finally {
        setLoading(false);
      }
    },
    [intl, notify],
  );

  return { exportOrders, loading };
};
